import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Lock, Shield, BookLock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ContentVault } from './ContentVault';

export const FamilySettings: React.FC = () => {
  const { user } = useAuth();

  // PIN state
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin]       = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // Lock toggles
  const [vaultLock,        setVaultLock]        = useState(false);  // parent_lock column
  const [requirePinRules,  setRequirePinRules]  = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    supabase
      .from('profiles')
      .select('parent_lock, pin_hash, require_pin_for_rules')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setVaultLock(Boolean((data as any)?.parent_lock));
        setHasPin(Boolean((data as any)?.pin_hash));
        setRequirePinRules(Boolean((data as any)?.require_pin_for_rules));
        setLoading(false);
      });
  }, [user?.id]);

  // ── Vault lock toggle ───────────────────────────────────────────────────────
  const handleVaultLock = async (next: boolean) => {
    if (!user?.id) return;
    if (next && !hasPin) {
      toast.error('Set a PIN below before turning on vault protection.');
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ parent_lock: next } as any)
      .eq('id', user.id);
    setSaving(false);
    if (error) { toast.error(`Failed: ${error.message}`); return; }
    setVaultLock(next);
    toast.success(next ? 'Vault lock enabled' : 'Vault lock disabled');
  };

  // ── Rule editing lock toggle ────────────────────────────────────────────────
  const handleRulesLock = async (next: boolean) => {
    if (!user?.id) return;
    if (next && !hasPin) {
      toast.error('Set a PIN below before turning on rule editing protection.');
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ require_pin_for_rules: next } as any)
      .eq('id', user.id);
    setSaving(false);
    if (error) { toast.error(`Failed: ${error.message}`); return; }
    setRequirePinRules(next);
    toast.success(next ? 'Rule editing lock enabled' : 'Rule editing lock disabled');
  };

  // ── Set / change PIN ────────────────────────────────────────────────────────
  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { toast.error('PIN must be exactly 4 digits.'); return; }
    if (pin !== confirmPin)   { toast.error('PINs do not match.'); return; }
    setSaving(true);
    const { error } = await supabase.rpc('set_parent_lock_pin', { p_pin: pin });
    setSaving(false);
    if (error) { toast.error(`Failed to set PIN: ${error.message}`); return; }
    setPin(''); setConfirmPin('');
    setHasPin(true);
    toast.success(hasPin ? 'PIN updated' : 'PIN set — vault protection is now available');
  };

  const handleClearPin = async () => {
    if (!confirm('Clear your vault PIN? This also turns off all PIN locks.')) return;
    setSaving(true);
    const { error } = await supabase.rpc('clear_parent_lock_pin');
    setSaving(false);
    if (error) { toast.error(`Failed: ${error.message}`); return; }
    setHasPin(false); setVaultLock(false); setRequirePinRules(false);
    toast.success('PIN cleared');
  };

  return (
    <div className="space-y-8">

      {/* ── Intro ──────────────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold">Family &amp; Content Protection</h2>
        <p className="text-sm text-muted-foreground mt-1">
          SocialScoop never deletes filtered content — it goes to your Content Vault.
          Use the optional PIN to control who can see what was filtered out, or who can change the rules.
        </p>
      </div>

      {/* ── PIN setup ──────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {hasPin ? 'Change vault PIN' : 'Set vault PIN'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            A 4-digit PIN you choose. Used to unlock the Content Vault and (optionally) to gate rule editing.
            Set it once and both protections share the same code.
          </p>
          <form onSubmit={handleSetPin} className="space-y-3 max-w-xs">
            <div>
              <Label htmlFor="pin">New PIN</Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                placeholder="4 digits"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="pin-confirm">Confirm PIN</Label>
              <Input
                id="pin-confirm"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                placeholder="4 digits"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                autoComplete="new-password"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
                disabled={saving || pin.length !== 4 || confirmPin.length !== 4}
              >
                {hasPin ? 'Change PIN' : 'Set PIN'}
              </Button>
              {hasPin && (
                <Button type="button" variant="outline" size="sm" onClick={handleClearPin} disabled={saving}>
                  Clear PIN
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Lock toggles ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            PIN protections
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {!hasPin && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Set a PIN above to enable these protections.
            </p>
          )}

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-sm font-medium">Lock Content Vault</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Require PIN to open the vault and see filtered content.
              </p>
            </div>
            <Switch
              checked={vaultLock}
              onCheckedChange={handleVaultLock}
              disabled={loading || saving || !hasPin}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <BookLock className="h-3.5 w-3.5 text-muted-foreground" />
                Lock rule editing
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Require PIN to add or change filtering rules. Curated feed still works normally.
              </p>
            </div>
            <Switch
              checked={requirePinRules}
              onCheckedChange={handleRulesLock}
              disabled={loading || saving || !hasPin}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Content Vault viewer ───────────────────────────────────────────── */}
      <div className="space-y-3">
        <Separator />
        <ContentVault />
      </div>
    </div>
  );
};

export default FamilySettings;
